import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { adminService } from '../../../services/adminService';

type DestinationType = 'segment' | 'hubspot' | 'salesforce';

interface Destination {
  id?: string;
  type: DestinationType;
  config: Record<string, any>;
  enabled?: boolean;
  event_filters?: string[];
}

const DestinationsIntegration: React.FC = () => {
  const [items, setItems] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Destination>({ type: 'segment', config: {}, enabled: true, event_filters: [] });

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminService.listDestinations();
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load destinations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpsert = async () => {
    if (!form?.type) { toast.error('Type is required'); return; }
    setSaving(true);
    try {
      await adminService.upsertDestination(form);
      toast.success('Destination saved');
      setForm({ type: 'segment', config: {}, enabled: true, event_filters: [] });
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save destination');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    try {
      await adminService.deleteDestination(id);
      toast.success('Destination deleted');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete destination');
    }
  };

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">Destinations (Segment / CRM)</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor="dest-type" className="block text-xs text-gray-600 mb-1">Type</label>
          <select id="dest-type" className="border rounded px-3 py-2 w-full" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as DestinationType }))}>
            <option value="segment">Segment</option>
            <option value="hubspot">HubSpot</option>
            <option value="salesforce">Salesforce</option>
          </select>
        </div>
        <div>
          <label htmlFor="dest-enabled" className="block text-xs text-gray-600 mb-1">Enabled</label>
          <input id="dest-enabled" type="checkbox" checked={!!form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Config</label>
          <textarea
            className="border rounded px-3 py-2 w-full font-mono text-sm"
            rows={3}
            placeholder="JSON config (e.g., Segment writeKey)"
            value={JSON.stringify(form.config || {})}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              try {
                const obj = JSON.parse(e.target.value || '{}');
                setForm((f) => ({ ...f, config: obj }));
              } catch {
                // ignore invalid JSON while typing
              }
            }}
          />
          <div className="text-xs text-gray-500 mt-1">Example: <code>{'{'}"writeKey":"..."{'}'}</code></div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleUpsert} disabled={saving} className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save Destination'}</button>
      </div>

      <div className="pt-2 border-t">
        <h4 className="text-sm font-medium mb-2">Existing Destinations</h4>
        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : (
          <ul className="divide-y">
            {items.map((d) => (
              <li key={d.id || Math.random()} className="py-2 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">{d.type}</span>
                  <span className="ml-2">{d.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div className="flex gap-2">
                  <button className="px-2 py-1 text-sm rounded border" onClick={() => setForm({ id: d.id, type: d.type, config: d.config || {}, enabled: d.enabled, event_filters: d.event_filters || [] })}>Edit</button>
                  <button className="px-2 py-1 text-sm rounded border text-red-600" onClick={() => handleDelete(d.id)}>Delete</button>
                </div>
              </li>
            ))}
            {items.length === 0 && <li className="py-2 text-sm text-gray-500">No destinations yet</li>}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DestinationsIntegration;
